import { associateLocation, dissociateLocation } from '../../../../api/services/Riders';
import { trackFullStoryEvent } from '../../../../vendor/features/FullStory/FullStory.service';
import type { LocationDraft } from '../types';

type DispatchInput = {
  slug: string;
  drafts: LocationDraft[];
  originalLocationIds: number[];
};

export type DispatchResult = { kind: 'ok' } | { kind: 'partial-failure'; firstFailure: unknown };

const isSameLocationId = (a: unknown, b: unknown): boolean => Number(a) === Number(b);

const resolveOriginalLocationIdForReplacementChain = (
  draft: LocationDraft,
  drafts: LocationDraft[],
  originalLocationIds: number[]
): number | null => {
  let targetId = draft.formData.replaceLocation?.id ?? null;

  if (!targetId) {
    return null;
  }

  const visited = new Set<number>();

  while (!visited.has(targetId)) {
    visited.add(targetId);
    const intermediateDraft = drafts.find((candidate) => isSameLocationId(candidate.location.id, targetId));
    const nextTargetId = intermediateDraft?.formData.replaceLocation?.id;

    if (!nextTargetId) {
      break;
    }
    targetId = nextTargetId;
  }

  return originalLocationIds.includes(targetId) ? targetId : null;
};

const toAssociatePayload = (draft: LocationDraft, resolvedReplaceLocationId: number | null) => ({
  primary: draft.formData.primary ?? false,
  mustBeMet: draft.formData.mustBeMet ?? false,
  locationProtocol: draft.formData.mustBeMetProtocol?.locationProtocol,
  authorizedContactGroups: draft.formData.mustBeMetProtocol?.authorizedContactGroups,
  ...(resolvedReplaceLocationId &&
    draft.formData.replaceLocation?.effectiveAt && {
      replaceLocation: { id: resolvedReplaceLocationId, effectiveAt: draft.formData.replaceLocation.effectiveAt },
    }),
});

export const dispatchLocationDrafts = async ({
  slug,
  drafts,
  originalLocationIds,
}: DispatchInput): Promise<DispatchResult> => {
  const supersededLocationIds = new Set(
    drafts.map((d) => d.formData.replaceLocation?.id).filter((id): id is number => !!id)
  );

  const terminalDrafts = drafts.filter(
    (d) => (d.isNew || d.isDirty) && !supersededLocationIds.has(Number(d.location.id))
  );

  for (const draft of terminalDrafts) {
    const resolvedReplaceLocationId = resolveOriginalLocationIdForReplacementChain(draft, drafts, originalLocationIds);
    const response = await associateLocation(
      slug,
      draft.location.id,
      toAssociatePayload(draft, resolvedReplaceLocationId)
    );

    if (response.type === 'error') {
      return { kind: 'partial-failure', firstFailure: response };
    }

    if (draft.isNew) {
      trackFullStoryEvent('location_created', { has_must_be_met: draft.formData.mustBeMet ?? false });
    } else {
      trackFullStoryEvent('location_edited', { fields_changed: draft.changedFields ?? [] });
    }
  }

  const currentIds = new Set(drafts.map((d) => Number(d.location.id)));
  const removedIds = originalLocationIds.filter((id) => !currentIds.has(id));

  for (const id of removedIds) {
    const response = await dissociateLocation(slug, id);

    if (response.type === 'error') {
      return { kind: 'partial-failure', firstFailure: response };
    }
  }

  return { kind: 'ok' };
};
